import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ViewColumn } from "@mui/icons-material";
import { BALANCE_BUCKETS, OTHER_COLUMNS } from "./tableCols";

const NET_FIELDS = BALANCE_BUCKETS.map(({ field }) => field);
const OPEN_FIELDS = BALANCE_BUCKETS.map(({ field }) => `${field}_open`);

// hidden: list of hidden column fields. onChange receives the new list.
export default function ColumnChooser({ hidden, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const isShown = (field) => !hidden.includes(field);
  const setShown = (fields, shown) => {
    const rest = hidden.filter((field) => !fields.includes(field));
    onChange(shown ? rest : [...rest, ...fields]);
  };
  const allShown = (fields) => fields.every(isShown);
  const someShown = (fields) => fields.some(isShown) && !allShown(fields);

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<ViewColumn />}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        Columns
      </Button>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box p={2} minWidth={300}>
          <Stack direction="row" spacing={1} mb={1}>
            <Button
              size="small"
              onClick={() =>
                onChange([
                  ...hidden.filter((f) => !OPEN_FIELDS.includes(f)),
                  ...NET_FIELDS.filter((f) => !hidden.includes(f)),
                ])
              }
            >
              Open only
            </Button>
            <Button
              size="small"
              onClick={() =>
                onChange([
                  ...hidden.filter((f) => !NET_FIELDS.includes(f)),
                  ...OPEN_FIELDS.filter((f) => !hidden.includes(f)),
                ])
              }
            >
              Net only
            </Button>
            <Button size="small" onClick={() => onChange([])}>
              Show all
            </Button>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Balance</TableCell>
                <TableCell padding="checkbox" align="center">
                  <FormControlLabel
                    label="Net"
                    labelPlacement="top"
                    control={
                      <Checkbox
                        size="small"
                        checked={allShown(NET_FIELDS)}
                        indeterminate={someShown(NET_FIELDS)}
                        onChange={(e) => setShown(NET_FIELDS, e.target.checked)}
                      />
                    }
                  />
                </TableCell>
                <TableCell padding="checkbox" align="center">
                  <FormControlLabel
                    label="Open"
                    labelPlacement="top"
                    control={
                      <Checkbox
                        size="small"
                        checked={allShown(OPEN_FIELDS)}
                        indeterminate={someShown(OPEN_FIELDS)}
                        onChange={(e) =>
                          setShown(OPEN_FIELDS, e.target.checked)
                        }
                      />
                    }
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {BALANCE_BUCKETS.map(({ field, label }) => (
                <TableRow key={field}>
                  <TableCell>{label}</TableCell>
                  {[field, `${field}_open`].map((f) => (
                    <TableCell key={f} padding="checkbox" align="center">
                      <Checkbox
                        size="small"
                        checked={isShown(f)}
                        inputProps={{
                          "aria-label": `${label} ${
                            f === field ? "net" : "open"
                          }`,
                        }}
                        onChange={(e) => setShown([f], e.target.checked)}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Other columns
          </Typography>
          <Stack>
            {OTHER_COLUMNS.map(({ field, label }) => (
              <FormControlLabel
                key={field}
                label={label}
                control={
                  <Checkbox
                    size="small"
                    checked={isShown(field)}
                    onChange={(e) => setShown([field], e.target.checked)}
                  />
                }
              />
            ))}
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
